/**
 * Shared UI Components for Wireframe Prototypes
 * Loading states, skeletons, modals, toasts, and form elements
 */

import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { X, Check, AlertTriangle, Info, Loader2, ChevronRight, Inbox, Search, FileX, AlertCircle, HelpCircle, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Link } from 'react-router-dom';

// ============================================================================
// LOADING & SKELETON COMPONENTS
// ============================================================================

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse bg-gray-200',
        className
      )}
    />
  );
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="bg-white border border-gray-200 p-5">
      <Skeleton className="h-4 w-1/3 mb-4" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn('h-3 mb-2', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="bg-white border border-gray-200">
      <div className="flex border-b border-gray-200 bg-gray-50 p-4">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1 mr-4 last:mr-0" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex border-b border-gray-200 last:border-b-0 p-4">
          {Array.from({ length: cols }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-3 flex-1 mr-4 last:mr-0" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonStats({ count = 4 }: { count?: number }) {
  return (
    <div className={cn('grid gap-4', `grid-cols-${count}`)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-gray-200 p-5">
          <Skeleton className="h-3 w-1/2 mb-3" />
          <Skeleton className="h-8 w-1/3" />
        </div>
      ))}
    </div>
  );
}

export function LoadingSpinner({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return <Loader2 className={cn('animate-spin text-accent-500', sizes[size], className)} />;
}

export function LoadingOverlay({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50">
      <div className="flex flex-col items-center gap-3">
        <LoadingSpinner size="lg" />
        <span className="text-sm text-gray-600">{message}</span>
      </div>
    </div>
  );
}

// ============================================================================
// TOAST NOTIFICATIONS
// ============================================================================

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}`;
    const newToast = { ...toast, id };
    setToasts(prev => [...prev, newToast]);

    // Auto-remove after duration
    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => onRemove(toast.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const icons = {
    success: <Check className="w-5 h-5 text-green-600" />,
    error: <X className="w-5 h-5 text-red-600" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-600" />,
    info: <Info className="w-5 h-5 text-blue-600" />,
  };

  const styles = {
    success: 'border-l-4 border-l-green-600',
    error: 'border-l-4 border-l-red-600',
    warning: 'border-l-4 border-l-amber-600',
    info: 'border-l-4 border-l-blue-600',
  };

  return (
    <div
      className={cn(
        'bg-white border border-gray-200 shadow-lg p-4 flex gap-3 items-start animate-in slide-in-from-right',
        styles[toast.type]
      )}
    >
      {icons[toast.type]}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-gray-900">{toast.title}</div>
        {toast.message && <div className="text-xs text-gray-600 mt-0.5">{toast.message}</div>}
      </div>
      <button onClick={onRemove} className="text-gray-400 hover:text-gray-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className={cn('relative bg-white w-full mx-4 max-h-[90vh] flex flex-col', sizes[size])}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FORM COMPONENTS
// ============================================================================

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function Select({ label, value, onChange, options, placeholder, className, disabled }: SelectProps) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500 disabled:bg-gray-100 disabled:text-gray-500"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface InputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number' | 'email' | 'tel';
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

export function Input({ label, value, onChange, placeholder, type = 'text', className, disabled, required }: InputProps) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className="w-full px-3 py-2 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500 disabled:bg-gray-100"
      />
    </div>
  );
}

interface TextAreaProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
}

export function TextArea({ label, value, onChange, placeholder, rows = 3, className, disabled }: TextAreaProps) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
        </label>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500 disabled:bg-gray-100 resize-none"
      />
    </div>
  );
}

// ============================================================================
// FILTER DROPDOWN (Enhanced)
// ============================================================================

interface FilterDropdownProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  className?: string;
}

export function FilterDropdown({ label, value, options, onChange, className }: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white border border-gray-200 px-4 py-2 min-w-[140px] text-left hover:border-gray-300 transition-colors"
      >
        <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[13px] font-semibold flex items-center justify-between gap-2">
          {value}
          <span className="text-[10px] text-gray-400">▼</span>
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-20 min-w-full">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2 text-left text-[13px] hover:bg-gray-50',
                  value === option && 'bg-accent-50 text-accent-500 font-medium'
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// CONFIRMATION DIALOG
// ============================================================================

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'default';
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
}: ConfirmDialogProps) {
  const buttonStyles = {
    danger: 'bg-red-600 text-white hover:bg-red-700',
    warning: 'bg-amber-500 text-white hover:bg-amber-600',
    default: 'bg-accent-500 text-white hover:bg-accent-600',
  };

  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-gray-600">{message}</p>
      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
        >
          {cancelText}
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={cn('px-4 py-2 text-sm font-medium flex items-center gap-2', buttonStyles[variant])}
        >
          {loading && <LoadingSpinner size="sm" className="text-white" />}
          {confirmText}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================================
// BREADCRUMBS
// ============================================================================

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  return (
    <nav className={cn('flex items-center gap-1.5 text-sm', className)}>
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5">
          {index > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          {item.href && index < items.length - 1 ? (
            <Link
              to={item.href}
              className="text-gray-500 hover:text-accent-500 transition-colors"
            >
              {item.label}
            </Link>
          ) : (
            <span className={cn(
              index === items.length - 1 ? 'font-medium text-gray-900' : 'text-gray-500'
            )}>
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
}

// ============================================================================
// EMPTY STATES
// ============================================================================

type EmptyStateVariant = 'no-data' | 'no-results' | 'error' | 'no-selection';

interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({ variant = 'no-data', title, message, action, className }: EmptyStateProps) {
  const icons = {
    'no-data': <Inbox className="w-12 h-12 text-gray-300" />,
    'no-results': <Search className="w-12 h-12 text-gray-300" />,
    'error': <FileX className="w-12 h-12 text-red-300" />,
    'no-selection': <AlertCircle className="w-12 h-12 text-gray-300" />,
  };

  const backgrounds = {
    'no-data': 'bg-gray-50',
    'no-results': 'bg-gray-50',
    'error': 'bg-red-50',
    'no-selection': 'bg-blue-50',
  };

  return (
    <div className={cn(
      'flex flex-col items-center justify-center py-12 px-6 text-center',
      backgrounds[variant],
      className
    )}>
      <div className="mb-4">{icons[variant]}</div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      {message && <p className="text-xs text-gray-500 max-w-xs mb-4">{message}</p>}
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 text-xs font-medium bg-accent-500 text-white hover:bg-accent-600"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// FORM VALIDATION
// ============================================================================

export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'pattern' | 'custom';
  value?: number | string | RegExp;
  message: string;
  validate?: (value: string) => boolean;
}

export interface FieldError {
  field: string;
  message: string;
}

export function validateField(value: string, rules: ValidationRule[]): string | null {
  for (const rule of rules) {
    switch (rule.type) {
      case 'required':
        if (!value || value.trim() === '') return rule.message;
        break;
      case 'minLength':
        if (value.length < (rule.value as number)) return rule.message;
        break;
      case 'maxLength':
        if (value.length > (rule.value as number)) return rule.message;
        break;
      case 'pattern':
        if (!(rule.value as RegExp).test(value)) return rule.message;
        break;
      case 'custom':
        if (rule.validate && !rule.validate(value)) return rule.message;
        break;
    }
  }
  return null;
}

// Enhanced Input with validation
interface ValidatedInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number' | 'email' | 'tel';
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  onBlur?: () => void;
}

export function ValidatedInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  className,
  disabled,
  required,
  error,
  hint,
  onBlur,
}: ValidatedInputProps) {
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          setTouched(true);
          onBlur?.();
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full px-3 py-2 border bg-white text-[13px] outline-none transition-colors',
          showError
            ? 'border-red-500 focus:border-red-500'
            : 'border-gray-200 focus:border-accent-500',
          disabled && 'bg-gray-100'
        )}
      />
      {showError && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
      {hint && !showError && (
        <p className="mt-1 text-xs text-gray-400">{hint}</p>
      )}
    </div>
  );
}

// Enhanced Select with validation
interface ValidatedSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
}

export function ValidatedSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
  required,
  error,
}: ValidatedSelectProps) {
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        disabled={disabled}
        className={cn(
          'w-full px-3 py-2 border bg-white text-[13px] outline-none transition-colors',
          showError
            ? 'border-red-500 focus:border-red-500'
            : 'border-gray-200 focus:border-accent-500',
          disabled && 'bg-gray-100 text-gray-500'
        )}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {showError && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// Form hook for managing validation state
export function useFormValidation<T extends Record<string, string>>(
  initialValues: T,
  validationRules: Partial<Record<keyof T, ValidationRule[]>>
) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const setValue = useCallback((field: keyof T, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const setFieldTouched = useCallback((field: keyof T) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    // Validate on blur
    const rules = validationRules[field];
    if (rules) {
      const error = validateField(values[field], rules);
      setErrors(prev => ({ ...prev, [field]: error || undefined }));
    }
  }, [values, validationRules]);

  const validateAll = useCallback(() => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    for (const field in validationRules) {
      const rules = validationRules[field];
      if (rules) {
        const error = validateField(values[field as keyof T], rules);
        if (error) {
          newErrors[field as keyof T] = error;
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    setTouched(Object.keys(validationRules).reduce((acc, key) => {
      acc[key as keyof T] = true;
      return acc;
    }, {} as Partial<Record<keyof T, boolean>>));

    return isValid;
  }, [values, validationRules]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    validateAll,
    reset,
    isValid: Object.keys(errors).length === 0,
  };
}

// ============================================================================
// UNSAVED CHANGES CONFIRMATION
// ============================================================================

interface UnsavedChangesModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSave?: () => void;
}

export function UnsavedChangesModal({ open, onClose, onDiscard, onSave }: UnsavedChangesModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="Unsaved Changes" size="sm">
      <p className="text-sm text-gray-600">
        You have unsaved changes. Do you want to save them before leaving?
      </p>
      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={onDiscard}
          className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50"
        >
          Discard Changes
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50"
        >
          Keep Editing
        </button>
        {onSave && (
          <button
            onClick={onSave}
            className="px-4 py-2 text-sm font-medium bg-accent-500 text-white hover:bg-accent-600"
          >
            Save & Close
          </button>
        )}
      </div>
    </Modal>
  );
}

// ============================================================================
// INLINE ALERT
// ============================================================================

interface InlineAlertProps {
  type: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
  className?: string;
  onDismiss?: () => void;
}

export function InlineAlert({ type, title, message, className, onDismiss }: InlineAlertProps) {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };

  const icons = {
    info: <Info className="w-4 h-4" />,
    success: <Check className="w-4 h-4" />,
    warning: <AlertTriangle className="w-4 h-4" />,
    error: <AlertCircle className="w-4 h-4" />,
  };

  return (
    <div className={cn('border p-3 flex items-start gap-3', styles[type], className)}>
      <div className="flex-shrink-0 mt-0.5">{icons[type]}</div>
      <div className="flex-1 min-w-0">
        {title && <div className="font-semibold text-sm mb-0.5">{title}</div>}
        <div className="text-sm">{message}</div>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="flex-shrink-0 opacity-60 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// TOOLTIP / HELP ICON
// ============================================================================

interface TooltipProps {
  content: string;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export function Tooltip({ content, children, position = 'top', className }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        x: rect.left + rect.width / 2,
        y: position === 'top' ? rect.top : rect.bottom,
      });
      setIsVisible(true);
    }
  };

  const positionStyles = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowStyles = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent',
  };

  return (
    <div
      ref={triggerRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={showTooltip}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={showTooltip}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={cn(
            'absolute z-50 px-2.5 py-1.5 text-xs text-white bg-gray-900 whitespace-nowrap max-w-xs',
            positionStyles[position]
          )}
        >
          {content}
          <div className={cn('absolute w-0 h-0 border-4', arrowStyles[position])} />
        </div>
      )}
    </div>
  );
}

// Help icon with tooltip
interface HelpTooltipProps {
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  iconSize?: 'sm' | 'md';
}

export function HelpTooltip({ content, position = 'top', className, iconSize = 'sm' }: HelpTooltipProps) {
  const sizes = { sm: 'w-3.5 h-3.5', md: 'w-4 h-4' };

  return (
    <Tooltip content={content} position={position}>
      <button
        type="button"
        className={cn(
          'inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors',
          className
        )}
        aria-label="Help"
      >
        <HelpCircle className={sizes[iconSize]} />
      </button>
    </Tooltip>
  );
}

// Label with help tooltip
interface LabelWithHelpProps {
  label: string;
  helpText: string;
  required?: boolean;
  className?: string;
}

export function LabelWithHelp({ label, helpText, required, className }: LabelWithHelpProps) {
  return (
    <label className={cn('flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5', className)}>
      {label}
      {required && <span className="text-red-500">*</span>}
      <HelpTooltip content={helpText} />
    </label>
  );
}

// ============================================================================
// DATA REFRESH INDICATOR
// ============================================================================

interface DataRefreshIndicatorProps {
  lastUpdated: Date | string | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  autoRefresh?: boolean;
  autoRefreshInterval?: number; // in seconds
  className?: string;
  showRefreshButton?: boolean;
}

export function DataRefreshIndicator({
  lastUpdated,
  onRefresh,
  isRefreshing = false,
  autoRefresh = false,
  autoRefreshInterval = 60,
  className,
  showRefreshButton = true,
}: DataRefreshIndicatorProps) {
  const [timeAgo, setTimeAgo] = useState('');
  const [countdown, setCountdown] = useState(autoRefreshInterval);

  // Format relative time
  const formatTimeAgo = useCallback((date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 10) return 'Just now';
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  }, []);

  // Update time ago display
  useEffect(() => {
    if (!lastUpdated) {
      setTimeAgo('Never');
      return;
    }

    const date = typeof lastUpdated === 'string' ? new Date(lastUpdated) : lastUpdated;
    setTimeAgo(formatTimeAgo(date));

    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(date));
    }, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [lastUpdated, formatTimeAgo]);

  // Auto-refresh countdown
  useEffect(() => {
    if (!autoRefresh || !onRefresh) return;

    setCountdown(autoRefreshInterval);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          onRefresh();
          return autoRefreshInterval;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, autoRefreshInterval, onRefresh]);

  return (
    <div className={cn('flex items-center gap-2 text-xs text-gray-500', className)}>
      <span className="flex items-center gap-1.5">
        {isRefreshing ? (
          <>
            <RefreshCw className="w-3 h-3 animate-spin text-accent-500" />
            <span className="text-accent-500">Refreshing...</span>
          </>
        ) : (
          <>
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            <span>Updated {timeAgo}</span>
          </>
        )}
      </span>

      {autoRefresh && !isRefreshing && (
        <span className="text-gray-400">
          (auto-refresh in {countdown}s)
        </span>
      )}

      {showRefreshButton && onRefresh && !isRefreshing && (
        <button
          onClick={onRefresh}
          className="flex items-center gap-1 text-accent-500 hover:text-accent-600 transition-colors"
          aria-label="Refresh data"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      )}
    </div>
  );
}

// Compact version for card headers
interface RefreshBadgeProps {
  lastUpdated: Date | string | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

export function RefreshBadge({ lastUpdated, onRefresh, isRefreshing = false, className }: RefreshBadgeProps) {
  const [timeAgo, setTimeAgo] = useState('');

  const formatTimeAgo = useCallback((date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  }, []);

  useEffect(() => {
    if (!lastUpdated) {
      setTimeAgo('--');
      return;
    }

    const date = typeof lastUpdated === 'string' ? new Date(lastUpdated) : lastUpdated;
    setTimeAgo(formatTimeAgo(date));

    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(date));
    }, 30000);

    return () => clearInterval(interval);
  }, [lastUpdated, formatTimeAgo]);

  return (
    <button
      onClick={onRefresh}
      disabled={isRefreshing || !onRefresh}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border transition-colors',
        isRefreshing
          ? 'bg-accent-50 border-accent-200 text-accent-600'
          : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-300',
        !onRefresh && 'cursor-default',
        className
      )}
      title={onRefresh ? 'Click to refresh' : undefined}
    >
      <RefreshCw className={cn('w-2.5 h-2.5', isRefreshing && 'animate-spin')} />
      {isRefreshing ? 'Updating...' : timeAgo}
    </button>
  );
}

// Hook for managing refresh state
export function useDataRefresh(fetchFn: () => Promise<void>, initialFetch = true) {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchFn();
      setLastUpdated(new Date());
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchFn]);

  useEffect(() => {
    if (initialFetch) {
      refresh();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { lastUpdated, isRefreshing, refresh };
}

export default {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonStats,
  LoadingSpinner,
  LoadingOverlay,
  ToastProvider,
  useToast,
  Modal,
  Select,
  Input,
  TextArea,
  FilterDropdown,
  ConfirmDialog,
  Breadcrumbs,
  EmptyState,
  ValidatedInput,
  ValidatedSelect,
  useFormValidation,
  validateField,
  UnsavedChangesModal,
  InlineAlert,
  Tooltip,
  HelpTooltip,
  LabelWithHelp,
  DataRefreshIndicator,
  RefreshBadge,
  useDataRefresh,
};
